import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  CrewMember,
  CrewOption,
  CrewWeek,
  JobPerDiem,
  JobSummary,
  PerDiemConflict,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'

/**
 * Per diem with no hours.
 *
 * NW Lining pays per diem on days nobody works — a crew that travels to Dutch
 * Harbor draws it across the weekend whether or not a wrench is turned. A crew
 * day cannot express that: it wants a work type, hours and a shift, so
 * recording a Saturday through that screen would mean inventing hours that
 * were never worked.
 *
 * So this screen is deliberately smaller than the crew-day one. No work type,
 * no hours, no start and stop, no photo. Job, dates, people.
 *
 * THE JOB IS NEVER DEFAULTED. Guessing it from the crew's most recent day
 * would be right most of the time and wrong silently — and per diem is costed
 * to a job, so a wrong guess puts real money against the wrong number for days
 * nobody was there to notice.
 */

/** "2026-08-15" -> "Sat 15 Aug". Parsed as parts: `new Date('2026-08-15')` is
 *  UTC midnight and renders as the 14th west of Greenwich, which is exactly
 *  where these crews are. */
function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][
    new Date(y, m - 1, d).getDay()
  ]
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]
  return `${wd} ${d} ${mon}`
}

function isoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Sunday through Saturday of the week containing `today`, weekend last.
 *
 *  Periods end on a Saturday, so this is the same span the crew-week grid and
 *  the paper sheet both use. Offering the whole week rather than only the
 *  weekend matters because the other use of this screen is backfilling a per
 *  diem someone forgot to tick mid-week. */
function weekOf(today: Date): string[] {
  const sunday = new Date(today)
  sunday.setDate(today.getDate() - today.getDay())
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday)
    d.setDate(sunday.getDate() + i)
    return isoDate(d)
  })
}

/** LAST week and this one.
 *
 *  A pay period ends on Saturday, so a supervisor opening this on SUNDAY is
 *  looking at a fresh week with yesterday -- the Saturday they mean to claim --
 *  already outside it. Offering only the current week made the single most
 *  likely moment to use this screen the one moment it could not help.
 *
 *  Two weeks rather than a date picker: the point of this screen is ticking
 *  boxes with gloves on. Anything further back is a correction the office
 *  makes, and the server refuses days in a period Sage has already paid
 *  regardless of what this offers. */
function pickableWeeks(today = new Date()): { label: string; days: string[] }[] {
  const lastWeek = new Date(today)
  lastWeek.setDate(today.getDate() - 7)
  return [
    { label: 'Last week', days: weekOf(lastWeek) },
    { label: 'This week', days: weekOf(today) },
  ]
}

export function PerDiemOnly() {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Supervisors are restricted to their own crew server-side; office and admin
  // are not. This only shapes what the screen offers — the API is the rule.
  const canPickAnyone = user?.role === 'Office' || user?.role === 'Admin'

  const [jobs, setJobs] = useState<JobSummary[]>([])
  const [crew, setCrew] = useState<CrewMember[]>([])
  const [crewOptions, setCrewOptions] = useState<CrewOption[] | null>(null)
  const [pickedCrewId, setPickedCrewId] = useState<number | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [jobNo, setJobNo] = useState('')
  const [jobFilter, setJobFilter] = useState('')
  const [perDiem, setPerDiem] = useState<JobPerDiem | null>(null)

  const weeks = useMemo(() => pickableWeeks(), [])
  const [dates, setDates] = useState<string[]>([])
  const [keys, setKeys] = useState<string[]>([])

  // Per diem already recorded this week, so a day that is already paid can be
  // shown as such before it is ticked rather than as a 409 afterwards.
  const [paid, setPaid] = useState<Map<string, string>>(new Map())

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<PerDiemConflict[]>([])

  useEffect(() => {
    void (async () => {
      try {
        const [j, c] = await Promise.all([api.jobs(), api.crew()])
        setJobs(j)
        setCrew(c)
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Could not load')
      }
    })()
  }, [])

  // Named crews to start from. Without this an office or admin user is handed
  // the whole 138-strong field roster to find six people in — the same reason
  // the crew-day screen asks "Whose crew?" first. Failure is not fatal: the
  // full roster below still works, it is just slower to pick from.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const opts = await api.crewOptions()
        if (!cancelled) setCrewOptions(opts)
      } catch {
        if (!cancelled) setCrewOptions([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // What each person would draw on the chosen job. Fetched for the whole
  // roster at once so ticking someone is instant rather than a round trip.
  useEffect(() => {
    if (!jobNo) return setPerDiem(null)
    let cancelled = false
    void (async () => {
      try {
        const pd = await api.jobPerDiem(jobNo)
        if (!cancelled) setPerDiem(pd)
      } catch {
        if (!cancelled) setPerDiem(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [jobNo])

  // Existing per diem for the week, keyed "employeeKey|date".
  const loadPaid = useCallback(async () => {
    try {
      // ONE CALL PER WEEK. crewWeek is keyed on a week ending and
      // per_diem_by_day is a seven-slot array indexed WITHIN that week, so the
      // slots have to be mapped back through the week they came from. Reusing
      // one call for both would label last week's payments with this week's
      // dates -- worse than not showing them at all.
      const map = new Map<string, string>()
      const results = await Promise.all(
        weeks.map((w) => api.crewWeek(w.days[6])),
      )
      results.forEach((crews: CrewWeek[], wi) => {
        for (const c of crews) {
          for (const m of c.members) {
            m.per_diem_by_day.forEach((amount, i) => {
              if (amount !== null) {
                map.set(
                  `${m.sage_employee_key}|${weeks[wi].days[i]}`,
                  m.per_diem_job_by_day[i] ?? '',
                )
              }
            })
          }
        }
      })
      setPaid(map)
    } catch {
      // Not fatal. The server refuses duplicates regardless — this only
      // decides whether the refusal is visible before the tap or after it.
    }
  }, [weeks])

  useEffect(() => {
    void loadPaid()
  }, [loadPaid])

  const filteredJobs = useMemo(() => {
    const q = jobFilter.trim().toLowerCase()
    const list = q
      ? jobs.filter(
          (j) =>
            j.job_no.toLowerCase().includes(q) ||
            (j.job_description ?? '').toLowerCase().includes(q),
        )
      : jobs
    return list.slice(0, 40)
  }, [jobs, jobFilter])

  const selectedJob = jobs.find((j) => j.job_no === jobNo)

  /** The pool to tick from: a chosen crew, or the full roster. */
  const pickedCrew = crewOptions?.find(
    (c) => c.supervisor_employee_id === pickedCrewId,
  )
  const memberPool = pickedCrew ? pickedCrew.members : crew

  /** Switching crews clears the ticks rather than carrying them across.
   *
   *  The crew-day screen pre-selects the whole crew, because a crew that
   *  worked a day worked it together. This screen deliberately does not:
   *  per diem is money paid for days nobody was on site to witness, so a
   *  name that arrives already ticked is a name nobody chose. Starting
   *  empty costs one tap on "Everyone" and makes over-payment deliberate. */
  function chooseCrew(crewOption: CrewOption | null) {
    setPickedCrewId(crewOption?.supervisor_employee_id ?? null)
    setKeys([])
  }

  const rateFor = useCallback(
    (key: string) =>
      perDiem?.people.find((p) => p.sage_employee_key === key)?.daily_amount ?? null,
    [perDiem],
  )

  /** Days this person is already paid for, among the ticked dates. */
  const clashesFor = useCallback(
    (key: string) => dates.filter((d) => paid.has(`${key}|${d}`)),
    [dates, paid],
  )

  const eligible = useMemo(
    () => memberPool.filter((c) => rateFor(c.sage_employee_key) !== null),
    [memberPool, rateFor],
  )

  const total = useMemo(
    () =>
      keys.reduce((sum, k) => sum + Number(rateFor(k) ?? 0), 0) * dates.length,
    [keys, dates, rateFor],
  )

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((x) => x !== value) : [...list, value]
  }

  const blocked = keys.filter((k) => clashesFor(k).length > 0)
  const canSubmit =
    !!jobNo &&
    perDiem?.has_rate === true &&
    dates.length > 0 &&
    keys.length > 0 &&
    blocked.length === 0 &&
    !submitting

  async function submit() {
    setSubmitting(true)
    setError(null)
    setConflicts([])
    try {
      // One id per date, generated here so a retry after a dropped response
      // completes the weekend rather than recording it twice.
      const submissions: Record<string, string> = {}
      for (const d of dates) submissions[d] = crypto.randomUUID()

      // This screen applies the same days to everyone ticked, so the pairs are
      // the product. The endpoint takes pairs because the crew-week grid, which
      // ticks one box at a time, cannot express itself as a product.
      const result = await api.recordPerDiemDays({
        job_no: jobNo,
        entries: dates.flatMap((d) =>
          keys.map((k) => ({ sage_employee_key: k, date_worked: d })),
        ),
        submissions,
        client_captured_utc: new Date().toISOString(),
      })

      navigate('/field', {
        state: {
          flash:
            `Per diem recorded — ${result.person_days} day` +
            `${result.person_days === 1 ? '' : 's'} for ${result.people} ` +
            `${result.people === 1 ? 'person' : 'people'}, ` +
            `$${Number(result.total_amount).toFixed(2)}.`,
        },
      })
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = err.detail as
          | { message?: string; conflicts?: PerDiemConflict[] }
          | undefined
        if (detail?.conflicts) {
          setConflicts(detail.conflicts)
          setError(detail.message ?? 'Some of those days are already recorded.')
        } else {
          setError(err.message)
        }
        // Someone else may have recorded them since this screen loaded.
        void loadPaid()
      } else {
        setError('Could not record per diem.')
      }
      setSubmitting(false)
    }
  }

  return (
    <div className="stack">
      {loadError && <div className="alert alert-error">{loadError}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {conflicts.length > 0 && (
        <div className="card">
          <h2>Already recorded</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Nothing was saved. Untick these and try again.
          </p>
          <div className="table-wrap">
            <table>
              <tbody>
                {conflicts.map((c, i) => (
                  <tr key={i}>
                    <td>{c.display_name ?? c.sage_employee_key}</td>
                    <td>{dayLabel(c.date_worked)}</td>
                    <td className="mono">{c.job_no}</td>
                    <td className="num">${Number(c.amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Per diem only</h2>
        <p className="muted">
          For days the crew is away but not working. No hours are recorded — if
          they worked, record a crew day instead and tick per diem there.
        </p>
      </div>

      <div className="card">
        <h2>Job</h2>
        {selectedJob ? (
          <div className="stack">
            <div>
              <strong className="mono">{selectedJob.job_no}</strong>{' '}
              {selectedJob.job_description}
            </div>
            {perDiem?.has_rate ? (
              <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
                {perDiem.location_description ?? perDiem.location_code}
                {perDiem.is_site_rate && ' · site rate'} ·{' '}
                {perDiem.tiers
                  .map((t) => `${t.tier} $${Number(t.daily_amount).toFixed(2)}`)
                  .join(' · ')}
              </p>
            ) : (
              <div className="alert alert-warn">
                {perDiem?.reason ?? 'No per diem rate is set up for this job.'}
              </div>
            )}
            <button className="btn btn-secondary" onClick={() => setJobNo('')}>
              Change job
            </button>
          </div>
        ) : (
          <>
            <div className="form-field">
              <label htmlFor="jobFilter">Search</label>
              <input
                id="jobFilter"
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                placeholder="Job number or name"
                autoCapitalize="characters"
              />
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {filteredJobs.map((j) => (
                <button
                  key={j.job_no}
                  className="btn btn-secondary btn-block"
                  style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                  onClick={() => setJobNo(j.job_no)}
                >
                  <span className="mono">{j.job_no}</span>
                  <span className="muted" style={{ fontWeight: 400 }}>
                    {j.job_description}
                  </span>
                </button>
              ))}
              {filteredJobs.length === 0 && <p className="muted">No matching jobs.</p>}
            </div>
          </>
        )}
      </div>

      {perDiem?.has_rate && (
        <>
          <div className="card">
            <h2>Days ({dates.length})</h2>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.8125rem' }}>
              Weekends are the usual case, but any day works. Last week is
              offered because a pay period ends on Saturday — on a Sunday,
              yesterday is already in the week before.
            </p>
            {weeks.map((w) => (
              <div key={w.label} style={{ marginBottom: '0.75rem' }}>
                <div
                  className="muted"
                  style={{
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: 4,
                  }}
                >
                  {w.label}
                </div>
                {w.days.map((d) => {
                  const on = dates.includes(d)
                  const future = d > isoDate(new Date())
                  return (
                    <button
                      key={d}
                      className={`btn ${on ? '' : 'btn-secondary'} btn-block`}
                      style={{
                        justifyContent: 'flex-start',
                        marginBottom: 6,
                        // A day that has not happened yet stays visible but
                        // reads as unlikely. Per diem IS sometimes booked ahead
                        // for a crew already travelling, so this is a hint and
                        // not a rule.
                        opacity: future && !on ? 0.55 : 1,
                      }}
                      aria-pressed={on}
                      onClick={() => setDates(toggle(dates, d))}
                    >
                      <span style={{ width: '1.5rem' }}>{on ? '✓' : ''}</span>
                      {dayLabel(d)}
                      {future && (
                        <span
                          className="muted"
                          style={{ marginLeft: 'auto', fontSize: '0.75rem' }}
                        >
                          not yet
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>

          {crewOptions !== null && crewOptions.length > 0 && (
            <div className="card">
              <h2>{canPickAnyone ? 'Whose crew?' : 'Your crew'}</h2>
              <p className="muted" style={{ marginTop: 0, fontSize: '0.8125rem' }}>
                {canPickAnyone
                  ? 'Pick a crew to tick from its members, or work from the full field roster. Changing this clears anyone already ticked.'
                  : 'Your crew, including yourself. To record per diem for someone on another crew, ask the office.'}
              </p>
              {crewOptions.map((c) => (
                <button
                  key={c.supervisor_employee_id}
                  className={`btn ${
                    pickedCrewId === c.supervisor_employee_id ? '' : 'btn-secondary'
                  } btn-block`}
                  style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                  aria-pressed={pickedCrewId === c.supervisor_employee_id}
                  onClick={() => chooseCrew(c)}
                >
                  <span style={{ width: '1.5rem' }}>
                    {pickedCrewId === c.supervisor_employee_id ? '✓' : ''}
                  </span>
                  {c.supervisor_name}
                  <span className="muted" style={{ fontWeight: 400, marginLeft: 'auto' }}>
                    {c.members.length}
                  </span>
                </button>
              ))}
              {/* Office and admin only. A supervisor's roster is already
                  restricted to their own crew server-side, so this would list
                  the same people under a label promising more. Kept here
                  because a travel weekend spans crews more often than a work
                  day does — people ride together. */}
              {canPickAnyone && (
                <button
                  className={`btn ${pickedCrewId === null ? '' : 'btn-secondary'} btn-block`}
                  style={{ justifyContent: 'flex-start' }}
                  aria-pressed={pickedCrewId === null}
                  onClick={() => chooseCrew(null)}
                >
                  <span style={{ width: '1.5rem' }}>{pickedCrewId === null ? '✓' : ''}</span>
                  All field staff
                  <span className="muted" style={{ fontWeight: 400, marginLeft: 'auto' }}>
                    {crew.length}
                  </span>
                </button>
              )}
            </div>
          )}

          <div className="card">
            <h2>
              Crew ({keys.length})
              {pickedCrew && (
                <span className="muted" style={{ fontWeight: 400 }}>
                  {' '}
                  — {pickedCrew.supervisor_name}
                </span>
              )}
            </h2>
            {eligible.length > 1 && (
              <button
                className="btn btn-secondary btn-block"
                style={{ marginBottom: 12 }}
                onClick={() =>
                  setKeys(
                    keys.length === eligible.length
                      ? []
                      : eligible.map((c) => c.sage_employee_key),
                  )
                }
              >
                {keys.length === eligible.length ? 'Clear all' : `Everyone (${eligible.length})`}
              </button>
            )}
            {memberPool.map((c) => {
              const rate = rateFor(c.sage_employee_key)
              const on = keys.includes(c.sage_employee_key)
              const clashes = clashesFor(c.sage_employee_key)
              // Someone with no rate is shown but not tickable: an office user
              // on the roster draws no per diem, and a $0.00 line is worse
              // than an absent one.
              const disabled = rate === null
              return (
                <button
                  key={c.sage_employee_key}
                  className={`btn ${on ? '' : 'btn-secondary'} btn-block`}
                  style={{
                    justifyContent: 'flex-start',
                    marginBottom: 6,
                    opacity: disabled ? 0.55 : 1,
                  }}
                  aria-pressed={on}
                  disabled={disabled}
                  onClick={() => setKeys(toggle(keys, c.sage_employee_key))}
                >
                  <span style={{ width: '1.5rem' }}>{on ? '✓' : ''}</span>
                  {c.display_name}
                  <span style={{ marginLeft: 'auto', fontWeight: 400 }}>
                    {rate === null ? (
                      <span className="muted">no per diem</span>
                    ) : clashes.length > 0 ? (
                      <span className="badge badge-warn">
                        already paid {clashes.map(dayLabel).join(', ')}
                      </span>
                    ) : (
                      `$${Number(rate).toFixed(2)}`
                    )}
                  </span>
                </button>
              )
            })}
          </div>

          {blocked.length > 0 && (
            <div className="alert alert-warn">
              Some of the people ticked already have per diem on days you have
              chosen. Untick them, or untick those days.
            </div>
          )}

          {/* SAY WHAT THE NUMBER IS.

              The button used to read "Record 56 days" for 7 calendar days
              across 8 crew. Nobody worked 56 days -- 56 is the count of per
              diems, one per person per day. Calling it "days" beside a date
              list showing seven of them invites the reader to think they have
              picked something wrong, and on a screen that pays money that
              hesitation is expensive.

              So the arithmetic is spelled out above the button and the button
              names the unit it is actually counting. */}
          {dates.length > 0 && keys.length > 0 && (
            <p className="muted" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
              {dates.length} calendar day{dates.length === 1 ? '' : 's'} ×{' '}
              {keys.length} crew member{keys.length === 1 ? '' : 's'} ={' '}
              {dates.length * keys.length} per diem
              {dates.length * keys.length === 1 ? '' : 's'}
            </p>
          )}

          <button className="btn btn-block" disabled={!canSubmit} onClick={() => void submit()}>
            {submitting
              ? 'Recording…'
              : dates.length && keys.length
                ? `Record ${dates.length * keys.length} per diem${
                    dates.length * keys.length === 1 ? '' : 's'
                  } · $${total.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : 'Record per diem'}
          </button>
        </>
      )}
    </div>
  )
}
